import { h } from '@stencil/core'
import { createStore } from '@stencil/store'
import { noop } from 'lodash-es'

import { JSX } from '../../components'
import { ILayoutProps } from '../app'
import { ColorsType } from '../tailwind'

type ToastProps = Partial<JSX.UiToast> & { key?: string; class?: string }

export type ToastContent = ToastProps['content']

export const ToastStore = createStore({
  toasts: [] as ToastProps[],
})

let uniqueToastId = 0

export function addToast(toast: ToastProps) {
  const { onDismissed = noop } = toast
  toast.key = `toast-${uniqueToastId++}`
  toast.onDismissed = (...args) => {
    ToastStore.state.toasts = ToastStore.state.toasts.filter((t) => t !== toast)
    onDismissed(...args)
  }
  ToastStore.state.toasts = [...ToastStore.state.toasts, toast]
}

export function panelToast({ class: className = '', ...props }: ToastProps) {
  ToastStore.state.toasts = []
  addToast({
    delay: null,
    color: 'surface-upper',
    class: `${className} w-full self-center pt-3 -mt-2 px-5 rounded-t-none rounded-b-lg text-glyph-lighter`,
    ...props,
  })
}

export function flashToast(flash: ILayoutProps['flash'][0]) {
  addToast({
    delay: 1500,
    autoClose: flash.type !== 'alert',
    color: {
      alert: 'info-error',
      warn: 'info-warn',
      notice: 'info-notice',
      success: 'info-ok',
    }[flash.type] as ColorsType,
    content: ({ dismiss }) => (
      <span class='flex items-center spacing-x-2'>
        <span>{flash.text}</span>
        {flash.type === 'alert' && <ui-icon class='text-xl cursor-pointer' icon='x' onClick={() => dismiss()} />}
      </span>
    ),
  })
}

export function asyncToast(
  promise: Promise<any>,
  waitingText: string,
  doneText = 'Done!',
  errorText = 'An error occurred!',
  color = 'info-ok' as ColorsType
) {
  promise.catch(() => {
    flashToast({ text: errorText, type: 'alert' })
  })
  addToast({
    color,
    delay: promise,
    autoClose: false,
    content: ({ waiting, dismiss }) => {
      if (!waiting) {
        dismiss(1000)
      }
      return (
        <span class='flex -ml-2'>
          <span class='flex items-center spacing-x-1' hidden={!waiting}>
            <div class='flex justify-center items-center w-4 h-4'>
              <ui-spinner strokeWeight='3px' />
            </div>
            <span class='whitespace-nowrap'>{waitingText}</span>
          </span>
          <span class='flex items-center spacing-x-1' hidden={waiting}>
            <div class='flex justify-center items-center w-4 h-4'>
              <ui-icon icon='check' size='xl' />
            </div>
            <span class='whitespace-nowrap'>{doneText}</span>
          </span>
        </span>
      )
    },
  })
}
